<template>
    <BModal ref="approveModal" :title="$t('approveModal.title')" centered no-close-on-backdrop header-variant="primary" modal-class="no-border-modal" @hidden="stopTimers">
        <div class="text-center">
            <template v-if="state === 'waiting'">
                <p>{{ $t(instructionKey) }}</p>
                <div class="mb-3"><i class="mdi mdi-cellphone-key display-4 text-primary"></i></div>
                <p class="d-flex align-items-center justify-content-center gap-2 mb-1">
                    <span class="spinner-border spinner-border-sm"></span>
                    <span>{{ $t('approveModal.waiting') }}</span>
                </p>
                <p class="mb-0"><small class="text-muted">{{ $t('approveModal.expiresIn', { time: remaining }) }}</small></p>
            </template>
            <p v-else-if="state === 'approved'" class="d-flex align-items-center justify-content-center gap-2 mb-0">
                <span class="spinner-border spinner-border-sm"></span>
                <span>{{ $t('approveModal.approved') }}</span>
            </p>
            <p v-else class="text-danger mb-0">{{ message }}</p>
        </div>
        <template #footer="{ cancel }">
            <div class="d-flex justify-content-end w-100">
                <button type="button" class="btn btn-secondary" @click="cancel">{{ $t('common.cancel') }}</button>
            </div>
        </template>
    </BModal>
</template>

<script>
// Step-up of the account page when the second factor is the approval on a
// phone: the change is a request the phone must approve (Haxo Setup,
// biometrics). The modal polls the request and, once approved, hands its id
// to the action as the code; the server consumes it.
import { useUserStore } from '@/stores/userStore';
import { BModal } from 'bootstrap-vue-next';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const POLL_INTERVAL_MS = 2000;

export default {
    name: 'ApproveModal',
    components: {
        BModal
    },
    props: {
        action: {
            type: Function,
            required: true
        },
        // What the phone is asked to approve, named there the same way: the
        // removal of a trusted phone, or another change on the account.
        purpose: {
            type: String,
            default: 'account'
        }
    },
    setup(props) {
        const userStore = useUserStore();
        const { t } = useI18n();
        const instructionKey = computed(() => props.purpose === 'phone_removal'
            ? 'approveModal.instructionRemoval'
            : 'approveModal.instruction');

        const approveModal = ref(null);
        // waiting | approved | denied | expired
        const state = ref('waiting');
        const message = ref('');
        const remaining = ref('');

        let challengeId = null;
        let expiresAt = null;
        let pollTimer = null;
        let clockTimer = null;
        let polling = false;

        const stopTimers = () => {
            if (pollTimer) clearInterval(pollTimer);
            if (clockTimer) clearInterval(clockTimer);
            pollTimer = null;
            clockTimer = null;
        };

        const end = (newState, text) => {
            stopTimers();
            state.value = newState;
            message.value = text;
        };

        const updateRemaining = () => {
            if (!expiresAt) {
                remaining.value = '';
                return;
            }
            const seconds = Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000));
            remaining.value = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
            if (seconds === 0 && state.value === 'waiting') {
                end('expired', t('approveModal.expired'));
            }
        };

        const poll = async () => {
            if (polling || state.value !== 'waiting') return;
            polling = true;
            try {
                const status = await userStore.loginChallengeStatus(challengeId);
                switch (status) {
                    case 'approved':
                        end('approved', '');
                        // The approved request's id completes the change like a code.
                        hide();
                        await props.action(challengeId);
                        break;
                    case 'denied':
                        end('denied', t('approveModal.denied'));
                        break;
                    case 'expired':
                    case 'consumed':
                        end('expired', t('approveModal.expired'));
                        break;
                    default:
                        break;
                }
            } catch (error) {
                // An unknown request is over; anything else (network) is retried.
                if (error.response?.status === 404) {
                    end('expired', t('approveModal.expired'));
                }
            } finally {
                polling = false;
            }
        };

        const show = (mfa) => {
            challengeId = mfa.challenge_id;
            expiresAt = mfa.expires_at ? new Date(mfa.expires_at) : null;
            state.value = 'waiting';
            message.value = '';
            stopTimers();
            updateRemaining();
            pollTimer = setInterval(poll, POLL_INTERVAL_MS);
            clockTimer = setInterval(updateRemaining, 1000);
            approveModal.value.show();
        };

        const hide = () => {
            stopTimers();
            approveModal.value.hide();
        };

        return {
            approveModal,
            instructionKey,
            state,
            message,
            remaining,
            stopTimers,
            show,
            hide,
        };
    },
}
</script>

<style>
.no-border-modal .modal-header {
  border-bottom: none !important;
}

.no-border-modal .modal-footer {
  border-top: none !important;
}
</style>
